import routes from '@/app/routes'
import { afterCheckoutRoute } from '@/checkout/helpers/completing'
import { automaticLogin } from '@/helpers/Authentication'
import { authExternal } from '@/helpers/AuthExternal'
import { configCatRequest } from '@/helpers/ConfigCat'
import { environment } from '@/helpers/Environment'
import { resetDocumentTitle } from '@/helpers/MiscellaneousHelpers'
import { removeQueryParamPromoCode } from '@/helpers/PreloadPromoCodes'
import { prefixedKeys, removeQueryParam, removeQueryParams } from '@/helpers/QueryStringParameters'
import { isTrueish } from '@/helpers/StringHelpers'
import { openLoginModal } from '@/modals/loginModal'
import { isLoggedIn } from '@/state/Authentication'
import { isCurrentMember } from '@/state/Membership'
import store from '@/store/store'
import VueRouter from 'vue-router'

const router = new VueRouter({
  mode: 'history',
  routes,

  // Support seller prefixes.
  base: environment.seller?.slug,

  // @ts-ignore Does this do anything?
  saveScrollPosition: true,

  scrollBehavior(to, from) {
    if (to.hash) {
      return { selector: to.hash }
    }

    // If in an iframe, then tell the parent to scroll to top.
    if (window.self !== window.top) {
      window.parent.postMessage(
        {
          message: 'scrollTop',
        },
        '*',
      )
    }

    if (to.name !== from.name) {
      return { x: 0, y: 0 }
    }
  },
})

router.afterEach((to, from) => {
  if (to.name !== 'event') {
    store.commit('clearUpsellsChain')
  }
})

// Strip trailing slash from URL
router.beforeEach((to, from, next) => {
  // to.path for the homepage is "/" so ignore that case
  if (to.path.length > 1 && to.path.match(/\/$/) && !to.name?.match(/^error/)) {
    // Original implementation was to clone to object, then update the path (as path is readonly on to)
    // and then call .next with the cloned object.
    // structuredClone is failing on the (to) object, and it looks like we don't need to clone it anyway.
    // we can just update the path and call next passing new path and other properties from the original to object.

    return next({
      path: to.path.substring(0, to.path.length - 1),
      params: to.params,
      query: to.query,
      hash: to.hash,
    })
  }

  resetDocumentTitle()

  return next()
})

router.beforeEach((to, from, next) => {
  const config = window.tix.web?.redirects
  // See https://github.com/stqry/tix-web/blob/7a3673be7e8ee5dbb7dc6f50e27076e55ef10297/middleware/require_seller_prefix.go#L34-L36
  const sellerRequiredPaths = [
    new RegExp('/$'), // $ ensures root only and not just all URL prefixes.
    new RegExp('/events'), // event detail and listing routes.
    new RegExp('/checkout$'), // Allow direct checkout links and the completed order route.
  ]
  const isSellerSlugRequiredRoute = sellerRequiredPaths.some((path) => to.path.match(path))
  // window.tix.seller is undefined if no seller slug is present in the URL.
  if (config?.require_seller_prefix && !window.tix.seller && isSellerSlugRequiredRoute) {
    window.location.href = config.target
  } else {
    next()
  }
})

router.beforeEach((to, from, next) => {
  const config = window.tix.web?.redirects
  const homePaths = [
    new RegExp('/$'), // $ ensures root only and not just all URL prefixes.
    new RegExp('/events$'), // $ ensures event listing only and not event details.
  ]
  const isHomeRoute = homePaths.some((path) => to.path.match(path))
  if (config?.disable_home_route && isHomeRoute) {
    window.location.href = config.target
  } else {
    next()
  }
})

router.beforeEach((to, from, next) => {
  if (to.path === '/my') {
    // Redirect home route to 'membership' if user is a member, otherwise redirect to my profile
    const nextPath = isCurrentMember() ? '/my/membership' : '/my/profile'

    next({
      path: nextPath,
      query: to.query,
      hash: to.hash,
    })
  } else {
    next()
  }
})

// Store the indication to skip upsells from the `expresscheckout` query
// string parameter and strip it from the URL.
// "Upsells" is a good internal name, but is not suitable for customer-facing
// content, including URLs.
router.beforeEach((to, from, next) => {
  if (isTrueish(to.query.expresscheckout)) {
    window.tixAppState.skipUpsells = true
  }
  next(removeQueryParam(to, 'expresscheckout'))
})

/**
 * Automatic login allows members to login directly from links provided by the tenant.
 *
 * These are often used in membership renewal campaign emails, for example.
 */
router.beforeEach((to, from, next) => {
  const credentials: Dict<string> = {
    scan_code: to.query.member_code,
    zip_code: to.query.zip_code,
  }

  // TODO Use filterNamespacedProperties()?
  const prefix = 'login.'
  const names = prefixedKeys(prefix, to.query)

  for (const name of names) {
    const key = name.replace(prefix, '')
    credentials[key] = to.query[name]
  }

  automaticLogin(credentials).then(() => {
    // Wait for automatic login to complete before continuing.
    // This ensures compatibility with the login modal query string parameter; `?login=yes`
    next(
      removeQueryParams(to, [
        ...names,
        // Legacy names of older autologin query parameters.
        'member_code',
        'zip_code',
      ]),
    )
  })
})

// Handle auto login with external auth or auto open modal with `login` query parameter
router.beforeEach((to, from, next) => {
  // Store the login query parameters.
  const loginOnInit = isTrueish(to.query.login)
  const requireLogin = isTrueish(to.query['requirelogin'])

  // Remove the login query parameters and continue initializing the router.
  next(removeQueryParams(to, ['login', 'requirelogin']))

  if (isLoggedIn()) return

  configCatRequest.then(async (flags) => {
    const isFlagEnabled = flags.auto_login_external

    const { login, isExternalAuth } = authExternal

    // Attempt to authenticate with external providers when the `requirelogin` query parameter is set.
    const loginSucceeded = isFlagEnabled && isExternalAuth() && requireLogin && (await login(to.fullPath)).success

    // Open the login modal if the login query parameter was specified, and auto-login failed.
    if (loginOnInit && !loginSucceeded) openLoginModal()
  })
})

// Store any discount code query string parameter and strip it from the URL.
router.beforeEach(removeQueryParamPromoCode)

router.afterEach((to, from) => {
  if (from.name === 'checkout' || from.name === 'manage/checkout') {
    afterCheckoutRoute(to, from)
  }
})

export default router
